import React, { useMemo } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Minus,
  Plus,
  RotateCcw,
  MousePointer,
  Ruler,
  PenTool,
  Check,
  Undo2,
  EyeOff,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

interface PdfControlsProps {
  onFinishString?: () => void;
  isDrawingActive?: boolean;
  selectedStringOption: string;
  setSelectedStringOption: (val: string) => void;
}

export const PdfControls: React.FC<PdfControlsProps> = ({
  onFinishString,
  isDrawingActive,
  selectedStringOption,
  setSelectedStringOption,
}) => {
  const {
    pdfScale,
    setPdfScale,
    currentPageIndex,
    setCurrentPageIndex,
    numPages,
    appMode,
    setAppMode,
    pixelsPerFoot,
    undoLastWire,
    hideAllWiresOnCurrentPage,
    pvWireItems,
    setStatus,
  } = useApp();

  const stringOptions = useMemo(() => {
    const list: string[] = [];
    pvWireItems.forEach((item) => {
      const mpptKey = Object.keys(item).find((key) => key.toLowerCase().includes('mppt'));
      const mpptValue = mpptKey ? item[mpptKey] : null;
      if (mpptValue) {
        list.push(`${mpptValue} - Positive`);
        list.push(`${mpptValue} - Negative`);
      }
    });
    return Array.from(new Set(list));
  }, [pvWireItems]);

  const handleToolSelect = () => {
    setAppMode('SELECT');
  };

  const handleToolCalibrate = () => {
    setAppMode('CALIBRATE');
    setStatus('Click two points on the drawing with a known dimension to calibrate scale.', 'normal');
  };

  const handleToolDraw = () => {
    if (!pixelsPerFoot) {
      alert('Please calibrate the scale first!');
      setAppMode('CALIBRATE');
      setStatus('Click two points on the drawing to calibrate scale first.', 'normal');
      return;
    }
    setAppMode('DRAW_WIRE');
    setStatus('Click to draw string path points. Click "Finish" when done.', 'normal');
  };

  const handleClearWires = () => {
    if (confirm('Hide all drawn wires on this page? (They will remain saved in your estimated totals)')) {
      hideAllWiresOnCurrentPage();
    }
  };

  return (
    <div className="pdf-controls">
      {/* Page Navigation */}
      <button
        type="button"
        className="tool-button"
        title="Previous Page"
        disabled={currentPageIndex <= 0}
        onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
      >
        <ArrowLeft className="h-4 w-4" />
      </button>

      <span className="tool-label">
        {currentPageIndex + 1} / {numPages || 1}
      </span>

      <button
        type="button"
        className="tool-button"
        title="Next Page"
        disabled={currentPageIndex >= numPages - 1}
        onClick={() => setCurrentPageIndex(Math.min(numPages - 1, currentPageIndex + 1))}
      >
        <ArrowRight className="h-4 w-4" />
      </button>

      <div className="control-spacer" />

      {/* Zoom Controls */}
      <button
        type="button"
        className="tool-button"
        title="Zoom Out"
        onClick={() => setPdfScale((prev) => Math.max(0.3, prev - 0.2))}
      >
        <Minus className="h-4 w-4" />
      </button>

      <span className="tool-label">{Math.round(pdfScale * 100)}%</span>

      <button
        type="button"
        className="tool-button"
        title="Zoom In"
        onClick={() => setPdfScale((prev) => Math.min(3.0, prev + 0.2))}
      >
        <Plus className="h-4 w-4" />
      </button>

      <button
        type="button"
        className="tool-button"
        title="Reset Zoom"
        onClick={() => setPdfScale(0.5)}
      >
        <RotateCcw className="h-4 w-4" />
      </button>

      <div className="control-spacer" />

      {/* Drawing / Measurement Tools */}
      <button
        type="button"
        className={`tool-button ${appMode === 'SELECT' ? 'active-tool' : ''}`}
        title="Select / Pan Mode"
        onClick={handleToolSelect}
      >
        <MousePointer className="h-4 w-4" />
      </button>

      <button
        type="button"
        className={`tool-button ${appMode === 'CALIBRATE' ? 'active-tool' : ''}`}
        title="Calibrate Scale (Click two known points)"
        onClick={handleToolCalibrate}
      >
        <Ruler className="h-4 w-4" />
      </button>

      <button
        type="button"
        className={`tool-button ${appMode === 'DRAW_WIRE' ? 'active-tool' : ''}`}
        title="Draw Wire String"
        onClick={handleToolDraw}
      >
        <PenTool className="h-4 w-4" />
      </button>

      {isDrawingActive && (
        <button
          type="button"
          className="tool-button"
          style={{ background: 'var(--primary)', color: 'white' }}
          title="Finish & Save Current String"
          onClick={onFinishString}
        >
          <Check className="h-4 w-4" />
        </button>
      )}

      <button
        type="button"
        className="tool-button"
        title="Undo Last Wire"
        onClick={undoLastWire}
      >
        <Undo2 className="h-4 w-4" />
      </button>

      <button
        type="button"
        className="tool-button"
        title="Hide All Wires on Page"
        onClick={handleClearWires}
      >
        <EyeOff className="h-4 w-4" />
      </button>

      <div className="control-spacer" />

      {/* String Name Selector */}
      <select
        value={selectedStringOption}
        onChange={(e) => setSelectedStringOption(e.target.value)}
        style={{
          background: 'rgba(15, 23, 42, 0.85)',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '8px',
          padding: '6px 10px',
          fontSize: '0.85rem',
          outline: 'none',
          maxWidth: '180px',
        }}
      >
        <option value="">Custom (Prompt)</option>
        {stringOptions.length > 0 && (
          <optgroup label="From AI Schedule">
            {stringOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </div>
  );
};
